import React from "react";
import { Link } from "react-router-dom";
import { Clock, ArrowUpRight } from "lucide-react";
import { listArticles } from "@/blog/articles";
import { CATEGORIES } from "@/blog/categories";

/**
 * Related articles block, shown at the bottom of every article (before the CTA).
 *
 * Selection logic:
 *  1. Same-category articles first (most thematically aligned).
 *  2. Fill up to `max` with the most recent articles from any category.
 *  3. Always exclude the current article.
 *
 * Pure presentational + linking — no state, no fetches.
 */
export default function RelatedArticles({ currentId, currentCategory, lang = "ca", langPrefix = "", max = 3 }) {
  const all = listArticles().filter((a) => a.id !== currentId);
  const sameCat = all.filter((a) => a.category === currentCategory);
  const others = all.filter((a) => a.category !== currentCategory);
  const picks = [...sameCat, ...others].slice(0, max);

  if (picks.length === 0) return null;

  const blogBase = `${langPrefix || ""}/blog`;
  const heading = lang === "es" ? "Sigue explorando" : "Continua explorant";
  const subheading = lang === "es"
    ? "Otras lecturas para tu camino de activación"
    : "Altres lectures per al teu camí d'activació";

  return (
    <section
      className="mt-16 pt-12 border-t border-slate-200"
      data-testid="related-articles"
      aria-labelledby="related-heading"
    >
      <div className="mb-8">
        <p className="text-xs uppercase tracking-[0.18em] text-[#B4C5A4] font-sans-display mb-2">
          {subheading}
        </p>
        <h2
          id="related-heading"
          className="font-sans-display text-2xl sm:text-3xl text-[#0B2545] tracking-tight"
        >
          {heading}
        </h2>
      </div>

      <ul className="grid sm:grid-cols-3 gap-4">
        {picks.map((a) => {
          const v = a[lang];
          const cat = CATEGORIES[a.category];
          return (
            <li key={a.id}>
              <Link
                to={`${blogBase}/${v.slug}`}
                data-testid={`related-card-${a.id}`}
                className="group block h-full bg-white border border-slate-200 rounded-lg p-5 hover:border-[#0B2545]/30 hover:shadow-sm transition"
              >
                <div className="flex items-center justify-between gap-2 mb-3">
                  <span className="text-[10px] uppercase tracking-[0.18em] text-[#B4C5A4] font-sans-display font-medium truncate">
                    {cat ? cat.label[lang] : ""}
                  </span>
                  <ArrowUpRight
                    className="h-4 w-4 text-slate-300 group-hover:text-[#0B2545] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition flex-shrink-0"
                    strokeWidth={1.5}
                  />
                </div>
                <h3 className="font-sans-display text-base text-[#0B2545] tracking-tight leading-snug mb-2 line-clamp-3">
                  {v.title}
                </h3>
                <p className="text-xs text-slate-400 inline-flex items-center gap-1">
                  <Clock className="h-3 w-3" /> {a.readingMinutes} min
                </p>
              </Link>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
